import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { getAllProductsShop } from "../../redux/actions/product";
import styles from "../../styles/styles";
import ProductCard from "../Route/ProductCard/ProductCard";
import { backend_url, server } from "../../server";
import Ratings from "../Products/Ratings";
import { getAllEventsShop } from "../../redux/actions/event";
import ShopEventCard from "./ShopEventCard";

const ShopProfileData = ({ isOwner, shopId }) => {
  const { products } = useSelector((state) => state.products);
  const { events } = useSelector((state) => state.events);
  const { user } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [active, setActive] = useState(1);
  const [sellerServiceReviews, setSellerServiceReviews] = useState([]);
  const [sellerReviewStats, setSellerReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
  const [bundleRules, setBundleRules] = useState([]);

  const targetShopId = shopId || user?._id;

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "reviews") setActive(3);
    else if (tab === "bundles" && !isOwner) setActive(2);
    else if (tab === "events" && isOwner) setActive(2);
    else if (tab === "products") setActive(1);
  }, [searchParams, isOwner]);
  
  // Use ref to track previous shop ID to prevent duplicate calls
  const prevShopIdRef = React.useRef(null);

  useEffect(() => {
    // Only fetch if shop ID changed
    if (targetShopId && targetShopId !== prevShopIdRef.current) {
      prevShopIdRef.current = targetShopId;
      if (isOwner) {
        dispatch(getAllEventsShop(targetShopId));
      }
      dispatch(getAllProductsShop(targetShopId));
    }
  }, [dispatch, targetShopId, isOwner]);

  useEffect(() => {
    if (!targetShopId || isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${server}/shop/get-shop-info/${targetShopId}`);
        if (!cancelled) {
          setBundleRules(data?.shop?.bundleRules || []);
        }
      } catch {
        if (!cancelled) setBundleRules([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetShopId, isOwner]);

  useEffect(() => {
    if (!targetShopId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${server}/user/seller-reviews/${targetShopId}`);
        if (!cancelled && data?.success) {
          setSellerServiceReviews(data.reviews || []);
          setSellerReviewStats({
            averageRating: data.averageRating || 0,
            totalReviews: data.totalReviews || 0,
          });
        }
      } catch {
        if (!cancelled) {
          setSellerServiceReviews([]);
          setSellerReviewStats({ averageRating: 0, totalReviews: 0 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetShopId]);

  const allReviews =
    products?.flatMap((product) =>
      (product.reviews || []).map((review) => ({
        ...review,
        productName: product.name,
        productId: product._id,
      }))
    ) || [];

  const totalReviewCount =
    (sellerReviewStats.totalReviews || 0) + allReviews.length;

  // ❌ Don't block rendering entirely — remove this
  // if (!seller) return <div className="text-center py-10">Loading shop data...</div>;

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between">
        <div className="w-full flex">
          <div className="flex items-center" onClick={() => setActive(1)}>
            <h5
              className={`font-[600] text-[20px] ${active === 1 ? "text-[#CCBEA1]" : "text-[#333]"
                } cursor-pointer pr-[20px]`}
            >
              Seller Products
            </h5>
          </div>
          <div className="flex items-center" onClick={() => setActive(2)}>
            <h5
              className={`font-[600] text-[20px] ${active === 2 ? "text-[#CCBEA1]" : "text-[#333]"
                } cursor-pointer pr-[20px]`}
            >
              {isOwner ? "Running Events" : "Bundle Offers"}
              {!isOwner && bundleRules.length > 0 && (
                <span className="ml-1 text-sm font-normal text-slate-500">
                  ({bundleRules.length})
                </span>
              )}
            </h5>
          </div>

          <div className="flex items-center" onClick={() => setActive(3)}>
            <h5
              className={`font-[600] text-[20px] ${active === 3 ? "text-[#CCBEA1]" : "text-[#333]"
                } cursor-pointer pr-[20px]`}
            >
              Seller Reviews
              {totalReviewCount > 0 && (
                <span className="ml-1 text-sm font-normal text-slate-500">
                  ({totalReviewCount})
                </span>
              )}
            </h5>
          </div>
        </div>
        {isOwner && (
          <div>
            <Link to="/profile/dashboard">
              <div className={`${styles.button} !rounded-[4px] h-[42px]`}>
                <span className="text-[#fff]">Go Dashboard</span>
              </div>
            </Link>
          </div>
        )}
      </div>

      <br />

      {active === 1 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 md:gap-[20px] lg:grid-cols-3 lg:gap-[25px] xl:grid-cols-4 xl:gap-[20px] mb-12 border-0">
          {(products || [])
            .filter((product) => isOwner || Number(product?.stock || 0) > 0)
            .map((i, index) => (
            <ProductCard data={i} key={index} isShop={true} />
          ))}
        </div>
      )}

      {active === 2 && isOwner && (
        <div className="w-full">
          {events && events.length ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {events.map((event) => (
                <ShopEventCard key={event._id} data={event} />
              ))}
            </div>
          ) : (
            <h5 className="w-full rounded-2xl border border-dashed border-slate-200 /60 py-10 text-center text-[18px] text-slate-500">
              This seller has no upcoming events.
            </h5>
          )}
        </div>
      )}

      {active === 2 && !isOwner && (
        <div className="w-full">
          {bundleRules.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {bundleRules.map((rule, index) => (
                <div
                  key={`${rule.minItems}-${rule.discountPercent}-${index}`}
                  className="rounded-2xl border border-[#38513b]/20 bg-[#38513b]/5 p-5"
                >
                  <p className="text-lg font-semibold text-[#38513b]">
                    Buy {rule.minItems}+ different products
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    Save {rule.discountPercent}% on your order from this seller.
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <h5 className="w-full rounded-2xl border border-dashed border-slate-200 py-10 text-center text-[18px] text-slate-500">
              This seller has no live bundle offers right now.
            </h5>
          )}
        </div>
      )}

      {active === 3 && (
        <div className="w-full space-y-10">
          <div>
            <h4 className="text-base font-semibold text-slate-900 mb-1">Seller service</h4>
            <p className="text-sm text-slate-500 mb-3">
              Ratings from buyers about this seller&apos;s service (shipping, communication, overall experience).
            </p>
            {sellerReviewStats.totalReviews > 0 ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Ratings rating={sellerReviewStats.averageRating} />
                  <span className="text-sm text-slate-600">
                    {sellerReviewStats.averageRating.toFixed(1)} / 5 · {sellerReviewStats.totalReviews} review
                    {sellerReviewStats.totalReviews === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="space-y-4">
                  {sellerServiceReviews.map((item, index) => (
                    <div className="w-full flex my-2" key={item.orderId || `svc-${index}`}>
                      <img
                        src={`${backend_url}${item.buyer?.avatar || "default-avatar.png"}`}
                        className="w-[50px] h-[50px] rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = `${backend_url}default-avatar.png`;
                        }}
                        alt=""
                      />
                      <div className="pl-2">
                        <div className="flex w-full items-center flex-wrap gap-2">
                          <h2 className="font-[600] text-sm">{item.buyer?.name || "Customer"}</h2>
                          <Ratings rating={item.rating} />
                        </div>
                        {item.comment ? (
                          <p className="font-[400] text-[#000000a7] text-sm mt-1">{item.comment}</p>
                        ) : null}
                        <p className="text-[#000000a7] text-[13px] mt-1">
                          {item.createdAt ? String(item.createdAt).substring(0, 10) : "—"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 py-2">No seller service reviews yet.</p>
            )}
          </div>

          <div>
            <h4 className="text-base font-semibold text-slate-900 mb-1">Product reviews</h4>
            <p className="text-sm text-slate-500 mb-3">Feedback left on individual listings.</p>
            {allReviews.length > 0 ? (
              allReviews.map((item, index) => (
                <div className="w-full flex my-4" key={item.productId ? `${item.productId}-${index}` : index}>
                  <img
                    src={`${backend_url}${item.user?.avatar || "default-avatar.png"}`}
                    className="w-[50px] h-[50px] rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = `${backend_url}default-avatar.png`;
                    }}
                    alt=""
                  />
                  <div className="pl-2">
                    <div className="flex w-full items-center flex-wrap gap-2">
                      <h1 className="font-[600] pr-2">{item.user?.name || "Customer"}</h1>
                      <Ratings rating={item.rating} />
                    </div>
                    {item.productName && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        on{" "}
                        <Link
                          to={`/product/${item.productId}`}
                          className="text-[#38513b] hover:underline"
                        >
                          {item.productName}
                        </Link>
                      </p>
                    )}
                    <p className="font-[400] text-[#000000a7]">{item?.comment}</p>
                    <p className="text-[#000000a7] text-[14px]">
                      {item.createdAt ? item.createdAt.substring(0, 10) : "—"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 py-2">No product reviews yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopProfileData;
